
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { combineName } from '@shared/user';
import { Button, Stack, Text } from '@client/shared/design';
import { useForm, zodValidator, TextField } from '@client/shared/form/index.ts';
import {
  AuthGate,
  SignUpInputSchema,
  authPost,
  clearStashedGrant,
  invalidateSession,
} from '@client/shared/auth/index.ts';
import { stashOwnerName } from '@client/features/onboarding';
import { PATHS } from '@client/shared/router/paths.ts';

async function postSignup(email: string, password: string): Promise<{ ok: boolean; error?: string }> {
  const response = await authPost('/auth/signup', 'signup', { email, password });
  if (response.ok) return { ok: true };
  let error = 'Couldn\'t create your account. Try again.';
  try {
    const body = (await response.json()) as { error?: string };
    if (typeof body.error === 'string') error = body.error;
  } catch {
  }
  return { ok: false, error };
}

export function SignUp() {
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: { firstName: '', lastName: '', email: '', password: '' },
    validators: { onSubmit: zodValidator(SignUpInputSchema) },
    onSubmit: async ({ value }) => {
      setFormError(null);
      const result = await postSignup(value.email.trim(), value.password);
      if (!result.ok) {
        setFormError(result.error ?? 'Couldn\'t create your account. Try again.');
        return;
      }
      // Fresh identity: a leftover grant would belong to a previous account (same rule as SignIn).
      clearStashedGrant();
      stashOwnerName(combineName(value.firstName, value.lastName));
      await invalidateSession(queryClient);
    },
  });

  return (
    <AuthGate
      eyebrow="Step 1 of 2"
      heading="Create your account"
      intro={
        <>
          Free during beta. Already have one? <a href={PATHS.login}>Sign in.</a>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        noValidate
      >
        <Stack gap={4}>
          <form.Field name="firstName">
            {(field) => (
              <TextField
                field={field}
                label="First name"
                type="text"
                autoComplete="given-name"
                placeholder="Alex"
              />
            )}
          </form.Field>

          <form.Field name="lastName">
            {(field) => (
              <TextField
                field={field}
                label="Last name"
                type="text"
                autoComplete="family-name"
                placeholder="Agent"
              />
            )}
          </form.Field>

          <form.Field name="email">
            {(field) => (
              <TextField
                field={field}
                label="Email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@yourgroup.com"
              />
            )}
          </form.Field>

          <form.Field name="password">
            {(field) => (
              <TextField
                field={field}
                label="Password"
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
              />
            )}
          </form.Field>

          {formError && (
            <Text size="sm" tone="danger" role="alert">
              {formError}
            </Text>
          )}

          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(isSubmitting) => (
              <Button type="submit" size="lg" block loading={isSubmitting}>
                Start free
              </Button>
            )}
          </form.Subscribe>
        </Stack>
      </form>
    </AuthGate>
  );
}
